import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Chip,
  Typography,
} from '@mui/material';
import { Add, Search, Warehouse as WarehouseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import { usePermissions } from '../../../hooks/usePermissions';
import useDebounce from '../../../hooks/useDebounce';
import warehouseService from '../services/warehouseService';
import { companyService } from '../../companies';
import WarehouseModalForm from '../components/WarehouseModalForm';
import LoadingLogo from '../../../components/LoadingLogo';

const WarehouseList = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [page, setPage] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadWarehouses();
  }, [page, debouncedSearch, selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const response = await warehouseService.getAll({
        search: debouncedSearch,
        companyId: selectedCompany,
        page,
        limit: 10,
      });
      setWarehouses(response.data);
    } catch (error) {
      showSnackbar('Error al cargar bodegas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  const handleCreate = () => {
    setSelectedWarehouse(null);
    setOpenModal(true);
  };

  const handleSave = async (data) => {
    try {
      if (selectedWarehouse) {
        await warehouseService.update(selectedWarehouse.id, data);
        setWarehouses(prev => prev.map(w =>
          w.id === selectedWarehouse.id ? { ...w, ...data } : w
        ));
        showSnackbar('Bodega actualizada exitosamente', 'success');
      } else {
        await warehouseService.create(data);
        showSnackbar('Bodega creada exitosamente', 'success');
        if (page !== 1) setPage(1);
        else loadWarehouses();
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar bodega', 'error');
      throw error;
    }
  };

  return (
    <Box>
      <PageHeader
        title="Bodegas de Archivo"
        actions={hasPermission('warehouse.create') && (
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Nueva Bodega
          </Button>
        )}
      />

      <Box sx={{ bgcolor: '#ffffff', borderRadius: '14px', p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            placeholder="Buscar bodegas..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Empresa</InputLabel>
            <Select
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setPage(1);
              }}
              label="Empresa"
            >
              <MenuItem value="">Todas</MenuItem>
              {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : warehouses.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <WarehouseIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography>No hay bodegas registradas</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {warehouses.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((warehouse) => (
            <Grid item xs={12} sm={6} md={4} key={warehouse.id}>
              <Paper
                sx={{ p: 2, cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}
                onClick={() => navigate(`/warehouses/${warehouse.id}`)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WarehouseIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{warehouse.name}</Typography>
                </Box>
                <Chip 
                  label={warehouse.code} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                {warehouse.address && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    📍 {warehouse.address}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="caption">
                    {warehouse.company?.name}
                  </Typography>
                  <Chip 
                    label={`${warehouse._count?.box_warehouse || 0} cajas`} 
                    size="small" 
                    color="info"
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <WarehouseModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        warehouse={selectedWarehouse}
        companies={companies}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WarehouseList;
